// Metadata for enrollment stages + sources. Single source of truth.

import {
  MagnifyingGlass,
  CalendarCheck,
  Handshake,
  Pencil,
  Envelope,
  Clock,
  CheckCircle,
  Student,
  XCircle,
  ArrowUUpLeft,
  Archive,
  Sparkle,
  CalendarBlank,
} from "@phosphor-icons/react";

// Ordered stage list (the "pipeline order" — Kanban columns + progress bar).
export const STAGES = [
  "lead",
  "inquiry",
  "tour_scheduled",
  "tour_completed",
  "application_started",
  "application_submitted",
  "future_enrollment",
  "waitlisted",
  "offer",
  "enrollment_pending",
  // Legacy `accepted` records are normalized to `offer` by the Admissions
  // migration. Keep the key for pre-migration/demo rendering only.
  "accepted",
  "enrolled",
  "declined",
  "withdrawn",
  "lost_lead",
  "archived",
];

// Stages shown to parents as their timeline (skips negative states).
export const PARENT_TIMELINE_STAGES = [
  "inquiry",
  "tour_scheduled",
  "application_submitted",
  "waitlisted",
  "offer",
  "enrollment_pending",
  "enrolled",
];

// Stages parents can edit their record at.
export const PARENT_EDITABLE_STAGES = new Set(["inquiry", "application_started"]);

// Terminal (closed) states.
export const TERMINAL_STAGES = new Set([
  "enrolled", "declined", "withdrawn", "lost_lead", "archived",
]);

export const STAGE_META = {
  lead:                  { label: "New lead",              short: "Lead",      icon: Sparkle,         tone: "neutral" },
  inquiry:               { label: "Inquiry",               short: "Inquiry",   icon: MagnifyingGlass, tone: "neutral" },
  tour_scheduled:        { label: "Tour scheduled",        short: "Tour",      icon: CalendarCheck,   tone: "neutral" },
  tour_completed:        { label: "Tour completed",        short: "Visited",   icon: Handshake,       tone: "neutral" },
  application_started:   { label: "Application started",   short: "In app",    icon: Pencil,          tone: "neutral" },
  application_submitted: { label: "Application submitted", short: "Submitted", icon: Envelope,        tone: "neutral" },
  future_enrollment:     { label: "Future enrollment",     short: "Future",    icon: CalendarBlank,   tone: "warm" },
  waitlisted:            { label: "Waitlisted",            short: "Waitlist",  icon: Clock,           tone: "warm" },
  offer:                 { label: "Offer extended",        short: "Offer",     icon: CheckCircle,     tone: "positive" },
  enrollment_pending:    { label: "Enrollment pending",    short: "Pending",   icon: Clock,           tone: "positive" },
  accepted:              { label: "Offer extended",        short: "Offer",     icon: CheckCircle,     tone: "positive" },
  enrolled:              { label: "Enrolled",              short: "Enrolled",  icon: Student,         tone: "positive" },
  declined:              { label: "Declined",              short: "Declined",  icon: XCircle,         tone: "negative" },
  withdrawn:             { label: "Withdrawn",             short: "Withdrew",  icon: ArrowUUpLeft,    tone: "negative" },
  lost_lead:             { label: "Closed lead",           short: "Closed",    icon: XCircle,         tone: "negative" },
  archived:              { label: "Archived",              short: "Archived",  icon: Archive,         tone: "neutral" },
};

// Mirrors the server-enforced state machine. The server remains authoritative;
// this map simply prevents admins from being offered invalid buttons.
const NEXT_STAGES = {
  lead: ["inquiry", "tour_scheduled", "application_started", "lost_lead", "archived"],
  inquiry: ["tour_scheduled", "application_started", "lost_lead", "archived"],
  tour_scheduled: ["tour_completed", "application_started", "waitlisted", "lost_lead", "archived"],
  tour_completed: ["application_started", "application_submitted", "waitlisted", "lost_lead", "archived"],
  application_started: ["application_submitted", "waitlisted", "withdrawn", "lost_lead", "archived"],
  application_submitted: ["waitlisted", "offer", "declined", "lost_lead", "archived"],
  future_enrollment: ["waitlisted", "offer", "archived"],
  waitlisted: ["offer", "future_enrollment", "withdrawn", "lost_lead", "archived"],
  offer: ["enrollment_pending", "enrolled", "waitlisted", "lost_lead", "archived"],
  enrollment_pending: ["enrolled", "waitlisted", "withdrawn", "lost_lead", "archived"],
  // Compatibility only; the migration maps this to offer.
  accepted: ["enrollment_pending", "enrolled", "waitlisted", "lost_lead", "archived"],
  enrolled: ["withdrawn", "archived"],
  declined: ["archived"],
  withdrawn: ["archived"],
  lost_lead: ["archived"],
  archived: [],
};

export function allowedNextStages(stage) {
  return NEXT_STAGES[stage] || [];
}

// Tailwind classes per tone. Uses existing palette (sage primary, warm
// secondary, terracotta accent). No loud raw colours.
export const TONE_CLASSES = {
  neutral:  { bg: "bg-neutral-100",          color: "text-neutral-700",       ring: "border-neutral-200" },
  warm:     { bg: "bg-secondary/30",         color: "text-neutral-900",       ring: "border-secondary/50" },
  positive: { bg: "bg-primary/10",           color: "text-primary-hover",     ring: "border-primary/30" },
  negative: { bg: "bg-[#C6851E]/10",         color: "text-[#C6851E]",         ring: "border-[#C6851E]/40" },
};

export function stageTone(stage) {
  return TONE_CLASSES[STAGE_META[stage]?.tone || "neutral"];
}

export const SOURCES = ["website", "referral", "walk_in", "social_media", "google", "other"];
export const SOURCE_LABELS = {
  website: "Website",
  referral: "Referral",
  walk_in: "Walk-in",
  social_media: "Social media",
  google: "Google",
  other: "Other",
};

export function formatEnrollmentDate(iso) {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatPersonName(record) {
  const first = record.parent_first_name || "";
  const last = record.parent_last_name || "";
  return `${first} ${last}`.trim() || record.parent_email || "—";
}

export function formatChildName(record) {
  const first = record.child_first_name || "";
  const last = record.child_last_name || "";
  return `${first} ${last}`.trim() || "Child TBD";
}

// What a parent should see as their top-line status (simpler wording).
export function parentStatusLabel(stage) {
  switch (stage) {
    case "lead": return "We're reviewing your inquiry";
    case "inquiry": return "We've received your inquiry";
    case "tour_scheduled": return "Your tour is scheduled";
    case "tour_completed": return "Tour complete — next step awaits";
    case "application_started": return "Finish your application";
    case "application_submitted": return "Application received";
    case "waitlisted": return "On the waitlist";
    case "future_enrollment": return "Planning for a future start";
    case "offer":
    case "accepted": return "Offer extended 🎉";
    case "enrollment_pending": return "Enrollment in progress";
    case "enrolled": return "Enrolled — welcome!";
    case "declined": return "Not a fit at this time";
    case "withdrawn": return "Withdrawn";
    case "lost_lead": return "This inquiry is closed";
    case "archived": return "Archived";
    default: return "—";
  }
}
